import { Injectable, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export type MenuMode =
  | 'static'
  | 'overlay'
  | 'horizontal'
  | 'slim'
  | 'compact'
  | 'reveal'
  | 'drawer';

export type ColorScheme = 'light' | 'dark' | 'dim';

export interface AppConfig {
  inputStyle: string;
  colorScheme: ColorScheme;
  theme: string;
  ripple: boolean;
  menuMode: MenuMode;
  scale: number;
  menuTheme: string;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  public staticMenuDesktopInactive = signal<boolean>(false);
  public overlayMenuActive = signal<boolean>(false);
  public rightMenuVisible = signal<boolean>(false);
  public configSidebarVisible = signal<boolean>(false);
  public staticMenuMobileActive = signal<boolean>(false);
  public menuHoverActive = signal<boolean>(false);
  public searchBarActive = signal<boolean>(false);
  public sidebarActive = signal<boolean>(false);
  public anchored = signal<boolean>(false);

  config = signal<AppConfig>({
    ripple: true,
    inputStyle: 'outlined',
    menuMode: 'compact',
    colorScheme: 'light',
    theme: 'ireport',
    scale: 14,
    menuTheme: 'white',
  });

  private overlayOpen = new Subject<any>();

  overlayOpen$ = this.overlayOpen.asObservable();

  constructor() {
    effect(() => {
      const config = this.config();
      if (this.updateStyle(config)) {
        console.log('[TODO: CLEANUP] updateStyle', config);
      }
      this.changeScale(config.scale);
    });
  }

  updateStyle(config: AppConfig) {
    return (
      config.theme !== this.config().theme ||
      config.colorScheme !== this.config().colorScheme
    );
  }

  replaceThemeLink(href: string) {
    const id = 'theme-link';
    let themeLink = <HTMLLinkElement>document.getElementById(id);
    const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');

    themeLink.parentNode!.insertBefore(cloneLinkElement, themeLink.nextSibling);
    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
    });
  }

  changeScale(value: number) {
    document.documentElement.style.fontSize = `${value}px`;
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.overlayMenuActive.set(!this.overlayMenuActive());

      if (this.overlayMenuActive()) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.staticMenuDesktopInactive.set(!this.staticMenuDesktopInactive());
    } else {
      this.staticMenuMobileActive.set(!this.staticMenuMobileActive());

      if (this.staticMenuMobileActive()) {
        this.overlayOpen.next(null);
      }
    }
  }

  onOverlaySubmenuOpen() {
    this.overlayOpen.next(null);
  }

  showRightMenu() {
    this.rightMenuVisible.set(true);
  }

  showConfigSidebar() {
    this.configSidebarVisible.set(true);
  }

  toggleSearchBar() {
    this.searchBarActive.set(!this.searchBarActive());
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isSlim() {
    return this.config().menuMode === 'slim';
  }

  isCompact() {
    return this.config().menuMode === 'compact';
  }

  isHorizontal() {
    return this.config().menuMode === 'horizontal';
  }

  isMobile() {
    return !this.isDesktop();
  }
}
